import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { convertHtmlToLinkedIn } from "../components/createPost/linkedinCopy";
import SideBar from "../components/common/SideBar";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import PublishBottomSheet from "../components/common/PublishBottomSheet";
import UserAccountModal from "../components/common/UserAccountModal";
import Button from "../components/common/Button";
import DocumentPreview from "../components/createPost/components/DocumentPreview";
import { toFetchableUrl } from "../utils/imageFetch";
import { formatLocalDateTime, getTimezone } from "../utils/dates";
import { mediaTitleFrom, titleFromFilename } from "../utils/filename";
import useUserEmail from "../utils/useUserEmail";
import useLinkedInAccounts from "../utils/useLinkedInAccounts";
import { apiFetch } from "../utils/apiFetch";
import { readError } from "../utils/apiError";
import { copyImageToClipboard } from "../utils/clipboard";
import VideoPlayer from "../video/VideoPlayer";
import { publishFailed } from "../video/videoStatus";
import arrowLeft from "../assets/common/arrow-left.svg";
import copyIcon from "../assets/posts/copy.svg";
import downloadPost from "../assets/tips/downloadPost.svg";
import editIcon from "../assets/tips/edit.svg";
import publishIcon from "../assets/navbar/publish.svg";

export default function PostViewing() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const mobileEditorRef = useRef(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [linkedInTarget, setLinkedInTarget] = useState(null);
  const email = useUserEmail();
  const { accounts, isConnected: isLinkedInConnected } = useLinkedInAccounts(email);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isPublishSheetOpen, setIsPublishSheetOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imgCopied, setImgCopied] = useState(false);
  const [editorHtml, setEditorHtml] = useState("");
  const post = location?.state?.post || null;
  const [postStatus, setPostStatus] = useState(post?.status || "Draft");
  const [isRepublishConfirmOpen, setIsRepublishConfirmOpen] = useState(false);
  // Defined BEFORE useEffect that calls them
  const isHashtagLine = (text) => /#[\w\u0080-\uFFFF]+/.test(text);

  const applySegmentBorders = (editorEl, segments) => {
    if (!editorEl || !segments) return;

    editorEl.querySelectorAll("[data-divider]").forEach((el) => el.remove());

    const children = Array.from(editorEl.children).filter(
      (child) => !child.hasAttribute("data-divider")
    );

    let hookChildEl = null;

    children.forEach((child, index) => {
      const childText = (child.innerText || child.textContent || "").trim();

      child.classList.remove(
        "border-l-4", "border-Brand-neon-600", "border-Brand-light-blue-700",
        "border-Brand-red-600", "border-Brand-yellow-500", "pl-3"
      );
      child.style.marginTop = "";

      if (!childText) return;

      if (isHashtagLine(childText)) {
        child.classList.add("border-l-4", "border-Brand-yellow-500", "pl-3");
        child.style.marginTop = "4px";
        return;
      }

      const isHook = segments?.hook
        ? childText.startsWith(segments.hook.trim().slice(0, 30))
        : false;
      const isCta = segments?.cta
        ? childText.startsWith(segments.cta.trim().slice(0, 30))
        : false;

      if (isHook) {
        child.classList.add("border-l-4", "border-Brand-neon-600", "pl-3");
        child.style.marginTop = "";
        hookChildEl = child;
      } else if (isCta) {
        child.classList.add("border-l-4", "border-Brand-red-600", "pl-3");
        child.style.marginTop = "4px";
      } else {
        const ctaChildIndex = children.findIndex((c) =>
          segments?.cta && (c.innerText || "").trim().startsWith(segments.cta.trim().slice(0, 30))
        );
        const isPastCta = ctaChildIndex !== -1 && index > ctaChildIndex;
        if (isPastCta) {
          child.classList.add("border-l-4", "border-Brand-red-600", "pl-3");
          child.style.marginTop = "4px";
        } else {
          child.classList.add("border-l-4", "border-Brand-light-blue-700", "pl-3");
          child.style.marginTop = "4px";
        }
      }
    });

    // Dashed divider after hook
    if (hookChildEl) {
      const divider = document.createElement("div");
      divider.setAttribute("data-divider", "true");
      divider.setAttribute("contenteditable", "false");
      divider.style.cssText = "pointer-events: none; position: relative; line-height: 0; margin: 8px 0 4px 0;";
      divider.innerHTML = `<div style="border-top: 1px dashed #b0b0b0;"></div>`;
      hookChildEl.insertAdjacentElement("afterend", divider);
    }
  };

  // useEffect AFTER helper functions
  useEffect(() => {
    if (post) {
      const hashtagLine = (post.hashtags || []).map((h) => `#${h}`).join(" ");
      const fullText = [
        post.hook || "",
        post.body || "",
        post.cta || "",
        hashtagLine,
      ].filter(Boolean).join("\n");

      const lines = fullText.split("\n");
      const html = lines.map((line) => `<div>${line || "<br>"}</div>`).join("");
      const segments = { hook: post.hook, cta: post.cta, hashtags: post.hashtags };

      setEditorHtml(html);

      // Desktop
      if (editorRef.current) {
        editorRef.current.innerHTML = html;
        setTimeout(() => applySegmentBorders(editorRef.current, segments), 50);
      }

      // Mobile
      setTimeout(() => {
        if (mobileEditorRef.current) {
          mobileEditorRef.current.innerHTML = html;
          setTimeout(() => applySegmentBorders(mobileEditorRef.current, segments), 50);
        }
      }, 100);
    }
  }, [post]);

  const handleCopy = async () => {
    if (!editorRef.current) return;
    try {
      const clone = editorRef.current.cloneNode(true);
      clone.querySelectorAll("[data-divider]").forEach((el) => el.remove());
      const linkedInText = convertHtmlToLinkedIn(clone.innerHTML);
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([linkedInText], { type: "text/plain" }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const renderDocument = () => {
    if (!post.documentUrl) return null;
    return (
      <DocumentPreview
        url={post.documentUrl}
        filename={post.documentFilename}
      />
    );
  };

  const renderVideo = () => {
    if (!post.videoUrl) return null;
    return (
      <div className="rounded-lg overflow-hidden border border-black-40">
        <VideoPlayer key={post.videoUrl} src={post.videoUrl} posterUrl={post.videoPosterUrl} />
      </div>
    );
  };

  const renderVideoFailed = () =>
    publishFailed({ ...post, status: postStatus }) && (
      <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg px-4 py-3">
        {t("video.failed")}
      </div>
    );

  const handleDownload = async () => {
    try {
      const zip = new JSZip();
      const clone = editorRef.current?.cloneNode(true);
      clone?.querySelectorAll("[data-divider]").forEach((el) => el.remove());
      const plainText = clone?.innerText?.replace(/\n{3,}/g, "\n\n") || post?.text || "";
      zip.file("post.txt", plainText);

      if (post?.imageUrl) {
        try {
          const response = await fetch(post.imageUrl);
          const blob = await response.blob();
          zip.file("image.png", blob);
        } catch (err) {
          console.error("Image download failed:", err);
        }
      } else if (post?.documentUrl) {
        try {
          const response = await fetch(toFetchableUrl(post.documentUrl));
          const blob = await response.blob();
          zip.file(post.documentFilename || "document.pdf", blob);
        } catch (err) {
          console.error("Document download failed:", err);
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "post-download.zip");
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  useEffect(() => {
    if (accounts.length === 1) setLinkedInTarget(accounts[0].target);
  }, [accounts]);

  const handleEdit = () => {
    navigate("/create", { state: { editPost: post } });
  };

  const handlePublishClick = () => {
    if (postStatus === "Published") {
      setIsRepublishConfirmOpen(true);
    } else {
      handlePublish();
    }
  };

  if (!post) {
    return (
      <div className="flex flex-col h-screen bg-black-0 items-center justify-center">
        <p className="text-black-600 text-lg">No post found.</p>
        <Button
          variant="dark"
          onClick={() => navigate("/draft")}
          className="mt-4"
        >
          {t("buttons.back")}
        </Button>
      </div>
    );
  }

  const persistPublishMetadata = async () => {
    const hashtagLine = (post.hashtags || []).map((h) => `#${h}`).join(" ");
    const fullText = [post.hook, post.body, post.cta, hashtagLine]
      .filter(Boolean)
      .join("\n");
    const formData = new FormData();
    formData.append("id", post.id);
    formData.append("post", post.text || fullText);
    formData.append("hook", post.hook || "");
    formData.append("body", post.body || "");
    formData.append("cta", post.cta || "");
    formData.append("hashtags", JSON.stringify(post.hashtags || []));
    formData.append("target", linkedInTarget);
    formData.append("publication_date", formatLocalDateTime());
    formData.append("timezone", getTimezone());

    const response = await apiFetch(process.env.REACT_APP_UPDATE_POST_URL, {
      method: "PUT",
      body: formData,
    });
    if (!response.ok) throw new Error(await readError(response));
  };

  const handlePublish = async () => {
    if (!accounts.some((a) => a.target === linkedInTarget)) {
      setIsPublishSheetOpen(true);
      return;
    }
    // A video post never goes through the LinkedIn publisher (it would publish the text only).
    // Scheduling it for "now" lets the backend scheduler publish it with the video.
    if (post.videoUrl) {
      setIsPublishing(true);
      try {
        await persistPublishMetadata();
        setPublishSuccess(true);
        setPostStatus("Scheduled");
      } catch (err) {
        console.error("Scheduling the video post failed:", err);
      } finally {
        setIsPublishing(false);
      }
      return;
    }
    const payload = {
      oid: email,
      target: linkedInTarget,
      post_id: post.id,
      hook: post.hook || "",
      body: post.body || "",
      cta: post.cta || "",
      hashtags: post.hashtags || [],
    };

    if (post.documentUrl) {
      payload.document_url = post.documentUrl;
      payload.document_filename = post.documentFilename
        ? titleFromFilename(post.documentFilename)
        : mediaTitleFrom(post.hook);
    } else if (post.imageUrl) {
      payload.image_url = post.imageUrl;
    }

    setIsPublishing(true);
    try {
      const response = await apiFetch(process.env.REACT_APP_PUBLISH_POST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Publish failed");

      try {
        await persistPublishMetadata();
      } catch (err) {
        console.error("Published, but failed to record metadata:", err);
      }

      setPublishSuccess(true);
      setPostStatus("Published");
    } catch (err) {
      console.error("Publish failed:", err);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-black-0">
      {/* Navbar */}
      <Navbar 
        isSidebarOpen={isSidebarOpen} 
        setIsSidebarOpen={setIsSidebarOpen}
        onPublish={() => handlePublish()}
        canPublish={!!post?.hook && !isPublishing && isLinkedInConnected}
        onOpenAccountModal={() => setIsAccountModalOpen(true)}
      />

      <div className="flex flex-1 overflow-hidden">
        <SideBar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />

        <main className="flex-1 md:p-6 md:space-y-6 relative transition-all duration-300 md:ml-[125px]">

          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/draft")}
            className="m-4 md:!mx-0 md:m-0 md:mb-4 u-btn-sm"
          >
            <img src={arrowLeft} alt="back" className="w-4 h-4" />
            {t("buttons.back")}
          </Button>

          {/* ===== DESKTOP — two columns ===== */}
          <div className="hidden md:grid grid-cols-2 border border-gradient-04 rounded-2xl mt-4">

            {/* Left column — disabled placeholder */}
            <aside className="bg-black-20 rounded-tl-2xl rounded-bl-2xl p-4 shadow-sm flex flex-col">
              <div className="flex items-center justify-between border-b pb-2 mb-4">
                <h2 className="text-lg font-bold uppercase tracking-wide text-black-800">
                  {t("sources.header")}
                </h2>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center gap-3 opacity-40 pointer-events-none select-none">
                <div className="w-full h-8 bg-black-40 rounded-lg" />
                <div className="w-3/4 h-8 bg-black-40 rounded-lg" />
                <div className="w-full h-8 bg-black-40 rounded-lg" />
                <div className="w-2/3 h-8 bg-black-40 rounded-lg" />
                <p className="text-black-400 text-sm mt-4 text-center">
                  Analytics coming soon
                </p>
              </div>
            </aside>

            {/* Right column */}
            <section className="bg-white rounded-tr-2xl rounded-br-2xl p-4 shadow-sm flex flex-col gap-4">
              {/* Status badges */}
              <div className="flex gap-2 flex-wrap w-full justify-end">
                {(() => {
                  const statusStyles = {
                    Draft: "bg-black-20 text-black-600 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                    Scheduled: "bg-tertiary-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                    Published: "bg-success-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                  };
                  const status = postStatus;
                  return (
                    <span className={`px-2 py-1 text-xs font-bold rounded-lg ${statusStyles[status] || statusStyles.Draft}`}>
                      {status}
                    </span>
                  );
                })()}
                {post.status !== "Draft" && post.target && (
                  <span className="px-2 py-1 text-xs font-bold rounded-lg bg-secondary-500 text-black-800">
                    {(() => {
                      const account = accounts.find(a => a.target === post.target);
                      const label = account?.label || (post.target === "personal" ? "Personal" : post.target);
                      return label.length > 40 ? `${label.slice(0, 40)}…` : label;
                    })()}
                  </span>
                )}
              </div>
              {/* Editor */}
              <div className="border rounded-lg bg-gray-50">
                <div className="flex items-center justify-end gap-2 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg">
                  <Button
                    variant="icon"
                    type="button"
                    onClick={handleCopy}
                    aria-label={t("buttons.copy")}
                  >
                    <img src={copyIcon} alt="copy" className="w-4 h-4" />
                  </Button>
                </div>
                <div className="p-2 bg-black-0 rounded-br-lg rounded-bl-lg">
                  <div
                    ref={editorRef}
                    className="w-full min-h-[200px] bg-transparent text-gray-700 px-2 leading-normal overflow-y-auto break-words text-xl"
                    style={{ whiteSpace: "pre-wrap" }}
                  />
                </div>
              </div>

              {/* Image */}
              {post.imageUrl && (
                <div className="rounded-lg overflow-hidden border border-black-40">
                  <div className="flex justify-end p-2 bg-black-20">
                    <Button
                      variant="icon"
                      type="button"
                      onClick={async () => {
                        try {
                          await copyImageToClipboard(post.imageUrl);
                          setImgCopied(true);
                          setTimeout(() => setImgCopied(false), 2000);
                        } catch (err) {
                          console.error("Image copy failed:", err);
                        }
                      }}
                    >
                      <img src={copyIcon} alt="copy" className="w-4 h-4" />
                    </Button>
                  </div>
                  <img
                    src={post.imageUrl}
                    alt="post"
                    className="w-full object-contain"
                  />
                </div>
              )}
              {renderDocument()}
              {renderVideo()}
              {renderVideoFailed()}

              {/* Action buttons */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <Button
                  variant="ghost"
                  onClick={handleEdit}
                  className="flex-1 gap-2"
                >
                  {t("buttons.edit")}
                  <img src={editIcon} alt="edit" className="w-4 h-4" />
                </Button>
                <Button
                  variant="dark"
                  onClick={handleDownload}
                  className="flex-1 gap-2"
                >
                  {t("buttons.downloadPost")}
                  <img src={downloadPost} alt="download" className="w-5 h-5" />
                </Button>
              </div>
            </section>
          </div>

          {/* ===== MOBILE — single column ===== */}
          <div className="flex flex-col gap-4 px-4 pb-4 md:hidden">
            {/* Status badges */}
            <div className="flex gap-2 flex-wrap w-full justify-end">
              {(() => {
                const statusStyles = {
                  Draft: "bg-black-20 text-black-600 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                  Scheduled: "bg-tertiary-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                  Published: "bg-success-500 text-black-800 group-hover:bg-primary-500 group-hover:text-black-800 transition-all",
                };
                const status = postStatus;
                return (
                  <span className={`px-2 py-1 text-xs font-bold rounded-lg ${statusStyles[status] || statusStyles.Draft}`}>
                    {status}
                  </span>
                );
              })()}
              {post.status !== "Draft" && post.target && (
                <span className="px-2 py-1 text-xs font-bold rounded-lg bg-secondary-500 text-black-800">
                  {(() => {
                    const account = accounts.find(a => a.target === post.target);
                    const label = account?.label || (post.target === "personal" ? "Personal" : post.target);
                    return label.length > 40 ? `${label.slice(0, 40)}…` : label;
                  })()}
                </span>
              )}
            </div>
            {/* Editor */}
            <div className="border rounded-lg bg-gray-50">
              <div className="flex items-center justify-end gap-2 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg">
                <Button
                  variant="icon"
                  type="button"
                  onClick={handleCopy}
                >
                  <img src={copyIcon} alt="copy" className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-2 bg-black-0 rounded-br-lg rounded-bl-lg">
                <div
                  ref={mobileEditorRef} 
                  className="w-full min-h-[150px] bg-transparent text-gray-700 px-2 leading-normal break-words text-xl"
                  style={{ whiteSpace: "pre-wrap" }}
                />
              </div>
            </div>

            {/* Image */}
            {post.imageUrl && (
              <div className="rounded-lg overflow-hidden border border-black-40">
                <div className="flex justify-end p-2 bg-black-20">
                  <Button
                    variant="icon"
                    type="button"
                    onClick={async () => {
                      try {
                        await copyImageToClipboard(post.imageUrl);
                        setImgCopied(true);
                        setTimeout(() => setImgCopied(false), 2000);
                      } catch (err) {
                        console.error("Image copy failed:", err);
                      }
                    }}
                  >
                    <img src={copyIcon} alt="copy" className="w-4 h-4" />
                  </Button>
                </div>
                <img
                  src={post.imageUrl}
                  alt="post"
                  className="w-full object-contain"
                />
              </div>
            )}
            {renderDocument()}
            {renderVideo()}
            {renderVideoFailed()}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <Button
                variant="ghost"
                onClick={handleEdit}
                className="flex-1 gap-2"
              >
                {t("buttons.edit")}
                <img src={editIcon} alt="edit" className="w-4 h-4" />
              </Button>
              <Button
                variant="primary"
                onClick={handlePublishClick}
                disabled={!isLinkedInConnected || isPublishing}
                className="w-full gap-2"
              >
                {t("navbar.publish")}
                <img src={publishIcon} alt="publish" className="w-5 h-5" />
              </Button>
              <Button
                variant="dark"
                onClick={handleDownload}
                className="flex-1 gap-2"
              >
                {t("buttons.downloadPost")}
                <img src={downloadPost} alt="download" className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </main>
      </div>

      <Footer />

      {copied && (
        <div className="fixed bottom-6 right-6 bg-black-800 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {t("copyToClipboard.text")}
        </div>
      )}
      {imgCopied && (
        <div className="fixed bottom-6 right-6 bg-black-800 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {t("copyToClipboard.image")}
        </div>
      )}
      {publishSuccess && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-black-800">{post.videoUrl ? t("video.publishing.title") : t("publish.successTitle", "Post Published!")}</h2>
            <p className="text-black-600 text-center text-lg">{post.videoUrl ? t("video.publishing.message") : t("publish.successMessage", "Your post has been successfully published to LinkedIn.")}</p>
            <div className="flex flex-col gap-3 w-full mt-2">
              {!post.videoUrl && (
                <a
                  href="https://www.linkedin.com/feed/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0A66C2] hover:bg-[#004182] text-white rounded text-lg font-bold transition w-full"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452H17.21v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.985V9h3.105v1.561h.044c.432-.818 1.49-1.681 3.067-1.681 3.28 0 3.885 2.159 3.885 4.964v6.608zM5.337 7.433a1.805 1.805 0 1 1 0-3.61 1.805 1.805 0 0 1 0 3.61zM6.955 20.452H3.72V9h3.235v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  {t("publish.viewOnLinkedIn", "View on LinkedIn")}
                </a>
              )}
              <Button variant="ghost" onClick={() => setPublishSuccess(false)} className="w-full justify-center">
                {t("buttons.close", "Close")}
              </Button>
            </div>
          </div>
        </div>
      )}
      {isRepublishConfirmOpen && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4 flex flex-col gap-4">
            <h2 className="text-xl font-bold text-black-800">
              {t("publish.republishTitle")}
            </h2>
            <p className="text-black-600">
              {t("publish.republishMessage")}
            </p>
            <div className="flex flex-col gap-3">
              <Button
                variant="dark"
                onClick={() => { setIsRepublishConfirmOpen(false); handlePublish(); }}
                className="w-full justify-center"
              >
                {t("publish.republishConfirm")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setIsRepublishConfirmOpen(false)}
                className="w-full justify-center"
              >
                {t("buttons.cancel")}
              </Button>
            </div>
          </div>
        </div>
      )}
      <PublishBottomSheet
        isOpen={isPublishSheetOpen}
        onClose={() => setIsPublishSheetOpen(false)}
        onPublish={async () => { await handlePublish(true); setIsPublishSheetOpen(false); }}
        isPublishing={isPublishing}
        accounts={accounts}
        selectedAccount={linkedInTarget}
        onSelectAccount={(target) => setLinkedInTarget(target)}
        isLinkedInConnected={isLinkedInConnected}
        onOpenAccountModal={() => setIsAccountModalOpen(true)}
      />
      <UserAccountModal
        userName=""
        setUserName={() => {}}
        userEmail={email}
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
      />
    </div>
  );
}